/*
 * Grand Gather Registry (Pty) Ltd
 * Copyright (c) 2025-2026 Grand Gather Registry (Pty) Ltd. All rights reserved.
 * Registration number 2025/182063/07
 * Proprietary and confidential. Unauthorised copying, distribution or use is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './index.css';
import api from './api';
import { playSuccessSound, playErrorSound } from './utils/notificationSound';
import { subscribeToPush, unsubscribeFromPush } from './utils/pushNotifications';
import Landing from './pages/Landing';
import Login from './pages/Login';
import VendorLogin from './pages/VendorLogin';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Wallet from './pages/Wallet';
import EventWorkspace from './pages/EventWorkspace';
import Vendors from './pages/Vendors';
import VendorProfile from './pages/VendorProfile';
import VendorDashboard from './pages/VendorDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import AppLayout from './components/AppLayout';
import InvitationView from './pages/InvitationView';
import TicketView from './pages/TicketView';
import TicketPurchase from './pages/TicketPurchase';
import AdminDashboard from './pages/AdminDashboard';
import About from './pages/About';
import Team from './pages/Team';
import Contact from './pages/Contact';
import Cookies from './pages/Cookies';
import Careers from './pages/Careers';
import HelpSupport from './pages/HelpSupport';
import KgatsoEssentials from './pages/KgatsoEssentials';
import PreLaunch from './pages/PreLaunch';
import ThankYouCard from './pages/ThankYouCard';
import TermsConditions from './pages/TermsConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AcceptableUse from './pages/AcceptableUse';
import ShippingPolicy from './pages/ShippingPolicy';
import ReturnsPolicy from './pages/ReturnsPolicy';
import DataDeletion from './pages/DataDeletion';
import Brochure from './pages/Brochure';
import AppShowcase from './pages/AppShowcase';
import PitchDeck from './pages/PitchDeck';
import Partnerships from './pages/Partnerships';
import CorporateEvents from './pages/CorporateEvents';
import CorporateEventPublic from './pages/CorporateEventPublic';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import UpgradePlans from './pages/UpgradePlans';
import PaymentReceipt from './pages/PaymentReceipt';
import { ThemeProvider } from './ThemeContext';
import { AuthContext, useAuth } from './AuthContext';
import LoadingScreen from './components/LoadingScreen';

export { AuthContext, useAuth };

// Helper to check if user has vendor access
const isVendor = (user) => user?.role === 'vendor' || user?.role === 'both';
const isHost = (user) => user?.role === 'host' || user?.role === 'both';

// Plan ranking for gating (higher index = higher tier)
const VENDOR_PLAN_RANK = { free: 0, pro: 1, premium: 2 };
const HOST_PLAN_RANK = { free: 0, standard: 1, premium: 2 };

function ProtectedRoute({ children, vendorOnly, hostOnly, adminOnly, minVendorPlan, minHostPlan }) {
  const { user } = useAuth();
  if (!user) return <Navigate to={vendorOnly ? "/vendor-login" : "/login"} />;
  if (adminOnly && !user.is_admin && !user.is_staff) return <Navigate to="/dashboard" />;
  if (vendorOnly && !isVendor(user)) return <Navigate to="/dashboard" />;
  if (hostOnly && !isHost(user)) return <Navigate to="/vendor-dashboard" />;
  if (minVendorPlan) {
    const userRank = VENDOR_PLAN_RANK[user?.plan] ?? 0;
    const requiredRank = VENDOR_PLAN_RANK[minVendorPlan] ?? 0;
    if (userRank < requiredRank) return <Navigate to="/upgrade?type=vendor" />;
  }
  if (minHostPlan) {
    const userRank = HOST_PLAN_RANK[user?.host_plan] ?? 0;
    const requiredRank = HOST_PLAN_RANK[minHostPlan] ?? 0;
    if (userRank < requiredRank) return <Navigate to="/upgrade?type=host" />;
  }
  return children;
}

// Detect if app is running in installed PWA (standalone) mode
const isPWA = () => window.matchMedia('(display-mode: standalone)').matches;

function LandingGate() {
  const { user } = useAuth();
  if (isPWA()) {
    return <Navigate to={user ? '/dashboard' : '/login'} replace />;
  }
  return <Landing />;
}

function AppLayoutRoute() {
  return <AppLayout><Outlet /></AppLayout>;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [toastExiting, setToastExiting] = useState(false);
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('ggr_token');
    const stored = localStorage.getItem('ggr_user');
    if (token && stored) {
      setUser(JSON.parse(stored));
      api.get('/auth/me').then(r => {
        setUser(r.data);
        localStorage.setItem('ggr_user', JSON.stringify(r.data));
        subscribeToPush();
      }).catch(() => {
        localStorage.removeItem('ggr_token');
        localStorage.removeItem('ggr_user');
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('ggr_token', res.data.token);
    localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    subscribeToPush();
    return res.data.user;
  };

  const register = async (name, email, password, role) => {
    const res = await api.post('/auth/register', { name, email, password, role });
    localStorage.setItem('ggr_token', res.data.token);
    localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    subscribeToPush();
    return res.data.user;
  };

  const logout = () => {
    unsubscribeFromPush();
    localStorage.removeItem('ggr_token');
    localStorage.removeItem('ggr_user');
    setUser(null);
    window.location.href = '/';
  };

  const updateUser = (newUser) => {
    setUser(newUser);
    localStorage.setItem('ggr_user', JSON.stringify(newUser));
  };

  const showToast = (message, type = 'success') => {
    setToastExiting(false);
    setToast({ message, type });
    clearTimeout(toastTimeoutRef.current);
    if (localStorage.getItem('ggr_sound') !== 'off') {
      if (type === 'error') playErrorSound();
      else playSuccessSound();
    }
    toastTimeoutRef.current = setTimeout(() => {
      setToastExiting(true);
      setTimeout(() => {
        setToast(null);
        setToastExiting(false);
      }, 250);
    }, 3000);
  };

  if (loading) return <LoadingScreen />;

  // For 'both' role, default to dashboard (host view) — they can switch from there
  const getDefaultRedirect = (u) => {
    if (!u) return '/login';
    if (u.role === 'vendor') return '/vendor-dashboard';
    return '/dashboard'; // host or both
  };

  return (
    <ThemeProvider>
    <AuthContext.Provider value={{ user, login, register, logout, showToast, updateUser }}>
      <Router>
        <Routes>
          <Route path="/" element={<LandingGate />} />
          <Route path="/login" element={user ? <Navigate to={getDefaultRedirect(user)} /> : <Login />} />
          <Route path="/vendor-login" element={user ? <Navigate to={getDefaultRedirect(user)} /> : <VendorLogin />} />
          <Route path="/register" element={user ? <Navigate to={getDefaultRedirect(user)} /> : <Register />} />
          <Route path="/register/vendor" element={user ? <Navigate to={getDefaultRedirect(user)} /> : <Register isVendor />} />
          <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRedirect(user)} /> : <ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          {/* Authenticated routes with AppLayout chrome */}
          <Route element={<AppLayoutRoute />}>
            <Route path="/dashboard" element={<ProtectedRoute hostOnly><ErrorBoundary message="An error occurred while loading the dashboard."><Dashboard /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute hostOnly><ErrorBoundary message="An error occurred while loading the wallet."><Wallet /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/event/:id" element={<ProtectedRoute hostOnly><ErrorBoundary message="An error occurred while loading the event workspace."><EventWorkspace /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
            <Route path="/vendors/:id" element={<ProtectedRoute><ErrorBoundary message="An error occurred while loading the vendor profile."><VendorProfile /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/vendor-dashboard" element={<ProtectedRoute vendorOnly><ErrorBoundary message="An error occurred while loading the vendor dashboard."><VendorDashboard /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/corporate" element={<ProtectedRoute hostOnly><CorporateEvents /></ProtectedRoute>} />
            <Route path="/upgrade" element={<ProtectedRoute><UpgradePlans /></ProtectedRoute>} />
            <Route path="/receipt/:ref" element={<ProtectedRoute><ErrorBoundary message="An error occurred while loading the receipt."><PaymentReceipt /></ErrorBoundary></ProtectedRoute>} />
          </Route>
          {/* Public routes — no layout chrome */}
          <Route path="/invite/:shareLink" element={<InvitationView />} />
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Team />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/help" element={<HelpSupport />} />
          <Route path="/partners/kgatso-funerals" element={<KgatsoEssentials />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/returns-policy" element={<ReturnsPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/ticket/:ticketCode" element={<TicketView />} />
          <Route path="/buy-tickets/:eventSlug/:shareLink" element={<TicketPurchase />} />
          <Route path="/buy-tickets/:shareLink" element={<TicketPurchase />} />
          <Route path="/thank-you/:eventId/:guestToken" element={<ThankYouCard />} />
          <Route path="/join" element={<PreLaunch />} />
          <Route path="/brochure" element={<Brochure />} />
          <Route path="/showcase" element={<AppShowcase />} />
          <Route path="/pitch" element={<PitchDeck />} />
          <Route path="/partnerships" element={<Partnerships />} />
          <Route path="/corp-event/:shareLink" element={<CorporateEventPublic />} />
        </Routes>
      </Router>
      {toast && <div className={`toast toast-${toast.type}${toastExiting ? ' toast-exit' : ''}`}>{toast.message}</div>}
    </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
